import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Ban } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLoad, Loadable, SelectField, TextField, Status, RunButton, ErrorBox, can, fmt, COUNTRY_OPTIONS } from "@/components/control/kit";
import { V3Page, V3Aside, Field, Notice, V3Actions, useRun, ErrorLine } from "@/components/control/v3";

const DELIVERY = "Ein E-Mail-Dienst ist nicht eingerichtet: Die Einladung wird gespeichert und protokolliert, aber nicht automatisch zugestellt.";
// Derived on the server from status and expiry. While nothing is delivered and the member page is missing, no code can be redeemed.
const VALIDITY = {
  "gültig": (v) => `Code gültig bis ${fmt(v.until)} – derzeit nicht einlösbar: kein Versand eingerichtet, Einlöseseite der Mitglieder-App fehlt`,
  abgelaufen: (v) => `Abgelaufen am ${fmt(v.until)}`,
  ohne_code: () => "Ohne Einlösecode (vor Einführung der Einlösung angelegt) – nicht einlösbar",
  "wird_eingelöst": () => "Wird gerade eingelöst",
  "eingelöst": (v) => `Eingelöst am ${fmt(v.at)}`,
  widerrufen: (v) => (v.at ? `Widerrufen am ${fmt(v.at)}` : "Widerrufen"),
};
const validityText = (v) => (VALIDITY[v.state] ? VALIDITY[v.state](v) : v.state);

function Invitations() {
  const st = useLoad("/member-invitations");
  return (
    <Loadable state={st}>
      {(d) => (d.invitations.length > 0 && (
        <section className="space-y-2.5 pt-4" data-testid="member-invitations">
          <h2 className="text-lg font-bold text-brand-purple">Erfasste Einladungen</h2>
          {d.invitations.map((i) => (
            <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3" data-testid={`member-invitation-${i.id}`}>
              <span><span className="block font-semibold text-brand-purple">{i.first_name} · {i.email}</span>
                <span className="text-sm text-zinc-600">{i.country}{i.organization ? ` · ${i.organization}` : ""} · {fmt(i.created_at)}</span>
                {i.delivery && <span className="block text-xs text-[#8A5A00]" data-testid={`member-invitation-delivery-${i.id}`}>Zustellung: {i.delivery}</span>}
                {i.validity && <span className="block text-xs text-zinc-700" data-testid={`member-invitation-validity-${i.id}`}>Gültigkeit: {validityText(i.validity)}</span>}</span>
              <span className="flex items-center gap-2"><Status value={i.validity?.state === "abgelaufen" ? "abgelaufen" : i.status} testId={`member-invitation-status-${i.id}`} />
                {["ausstehend", "wird_eingelöst"].includes(i.status) && <RunButton label="Widerrufen" icon={Ban} testId={`revoke-invitation-${i.id}`} success="Einladung widerrufen."
                  onDone={st.reload} run={() => api.post(`/member-invitations/${i.id}/revoke`)} />}</span>
            </div>
          ))}
        </section>
      ))}
    </Loadable>
  );
}

// C40: invitation form, explicit review step, honest delivery note. Requires members:manage (also enforced by the server).
export default function InviteMember() {
  const { user } = useAuth();
  if (!can(user, "members:manage")) return <ErrorBox status={403} error="Mitglieder einladen dürfen nur Rollen mit Verwaltungsrecht für Mitglieder." />;
  return <InviteForm />;
}

function InviteForm() {
  const navigate = useNavigate();
  const orgs = useLoad("/organizations?status=freigegeben");
  const [v, setV] = useState({ first_name: "", email: "", country: "CH", organization_id: "" });
  const [review, setReview] = useState(false);
  const { busy, error, run } = useRun();
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));
  const orgList = (orgs.data?.organizations || []).filter((o) => o.status === "freigegeben");
  const orgName = orgList.find((o) => o.id === v.organization_id)?.name;
  const save = async () => {
    if (await run(() => api.post("/member-invitations", { ...v, organization_id: v.organization_id || null }), "Einladung gespeichert.")) {
      setReview(false);
      setV({ first_name: "", email: "", country: "CH", organization_id: "" });
    }
  };
  return (
    <V3Page crumb="Mitglieder" title="Mitglied einladen" subtitle="Prüfe die Einladung, bevor eine Nachricht an die Person gesendet wird."
      aside={<V3Aside subjectTitle="Ausgewählter Vorgang" subject={v.first_name || "Noch nicht eingegeben"}
        note="Vor dem Speichern Angaben und Zustellweg prüfen. Ohne E-Mail-Dienst wird nichts automatisch versendet." />} testId="invite-member-page">
      {review ? (
        <>
          <Field label="Vorname" testId="invite-review-name">{v.first_name}</Field>
          <Field label="E-Mail-Adresse" testId="invite-review-email">{v.email}</Field>
          <Field label="Land">{COUNTRY_OPTIONS.find((c) => c.value === v.country)?.label}</Field>
          <Field label="Einladende Organisation">{orgName || "Keine"}</Field>
          <Notice title="Zustellung" text={DELIVERY} testId="invite-delivery-note" />
          <ErrorLine error={error} testId="invite-error" />
          <V3Actions primary="Einladung speichern" onPrimary={save} busy={busy} secondary="Zurück zur Eingabe" onSecondary={() => setReview(false)} testId="invite-save" />
        </>
      ) : (
        <>
          <TextField id="invite-first-name" label="Vorname" value={v.first_name} onChange={set("first_name")} testId="invite-first-name" />
          <TextField id="invite-email" label="E-Mail-Adresse" type="email" value={v.email} onChange={set("email")} testId="invite-email" />
          <SelectField id="invite-country" label="Land" value={v.country} onChange={set("country")} options={COUNTRY_OPTIONS} testId="invite-country" />
          <SelectField id="invite-org" label="Einladende Organisation" value={v.organization_id} onChange={set("organization_id")} testId="invite-org"
            options={[{ value: "", label: "Keine" }, ...orgList.map((o) => ({ value: o.id, label: o.name }))]} />
          <V3Actions primary="Einladung prüfen" onPrimary={() => setReview(true)} primaryDisabled={!v.first_name.trim() || !v.email.includes("@")}
            onSecondary={() => navigate("/mitglieder")} testId="invite-review" />
        </>
      )}
      <Invitations key={busy ? "busy" : "idle"} />
    </V3Page>
  );
}
